import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';
import { map, publishReplay, refCount, retry } from 'rxjs/operators';

import { HttpHeaders, HttpParams } from '@angular/common/http';
import {
    BaseFlatFieldDefinition,
    BaseNestedFieldDefinition,
    DottedKey,
    FieldDefinition,
    FieldDefinitionValue,
    SearchParams,
    ShAbstractHttpService,
    ShFieldDefinition,
    ShFieldDefinitionService
} from '@stylehaven/components';
import { FieldDefinitionHelper } from '../../helpers/field-definition.helper';

@Injectable()
export class FieldDefinitionService extends ShFieldDefinitionService {
    readonly ENDPOINT: string = 'v1/fielddefinitions';
    readonly COLLAPSE_LONG_TRANSLATIONS: string = 'All';

    protected readonly FALLBACK_VALUE: BaseNestedFieldDefinition[] = [];
    protected readonly TYPE_NAME: string = 'FieldDefinition';
    protected readonly DEFAULT_HEADERS: HttpHeaders = new HttpHeaders();

    // Cache
    private fieldDefinitions$?: Observable<FieldDefinition[]>;
    protected flatFieldDefinitions$: Observable<BaseFlatFieldDefinition[]>;
    protected fullNestedFieldDefinitions$: Observable<BaseFlatFieldDefinition[]>;

    // Injectors
    protected httpService: ShAbstractHttpService = inject(ShAbstractHttpService);

    protected fetchFieldDefinitions(isNested: boolean): Observable<ShFieldDefinition[]> {
        const params: HttpParams = new HttpParams()
            .append('nested', isNested)
            .append('collapseLongTranslations', this.COLLAPSE_LONG_TRANSLATIONS);

        return this.httpService
            .get<BaseFlatFieldDefinition[]>(this.ENDPOINT, params, this.DEFAULT_HEADERS)
            .pipe(map(apiFieldDefinitions => this.enhanceWithParents(apiFieldDefinitions)));
    }

    getAll(): Observable<FieldDefinition[]> {
        if (!this.fieldDefinitions$) {
            this.fieldDefinitions$ = this.fetchFieldDefinitions(false).pipe(publishReplay(1), refCount());
        }

        return this.fieldDefinitions$;
    }
    getFlatFieldDefinitions(): Observable<BaseFlatFieldDefinition[]> {
        if (!this.flatFieldDefinitions$) {
            this.flatFieldDefinitions$ = this.getAll().pipe(retry(2), publishReplay(1), refCount());
        }

        return this.flatFieldDefinitions$;
    }

    // Used in advanced search
    getAllNested(): Observable<BaseNestedFieldDefinition[]> {
        if (!this.fullNestedFieldDefinitions$) {
            this.fullNestedFieldDefinitions$ = this.getFlatFieldDefinitions().pipe(
                map(flatFields =>
                    flatFields.map(def => {
                        return this.enhance(def);
                    })
                ),
                map(flatFields => FieldDefinitionHelper.nestFlatFieldDefinitions(flatFields)),
                map(fieldDefinitions => this.enhanceNestedWithParents(fieldDefinitions)),
                publishReplay(1),
                refCount()
            );
        }

        return this.fullNestedFieldDefinitions$;
    }

    private enhance(fieldDefinition: BaseFlatFieldDefinition): BaseFlatFieldDefinition {
        return {
            ...fieldDefinition,
            Label: fieldDefinition.Translations.nl,
            DisableSearchOnClick: true
        };
    }

    private enhanceNestedWithParents(fieldDefinitions: BaseNestedFieldDefinition[]): BaseNestedFieldDefinition[] {
        const idRefs: { [key: number]: BaseNestedFieldDefinition } = fieldDefinitions.reduce(
            (hashMap, item) => ({ ...hashMap, [item.FieldDefinitionId]: item }),
            {}
        );

        return fieldDefinitions.map(fieldDefinition => {
            if (fieldDefinition.ParentId) {
                fieldDefinition.Parent = idRefs[fieldDefinition.ParentId];
            }
            return fieldDefinition;
        });
    }

    getByDottedKeys(dottedKeys: string[]): Observable<BaseNestedFieldDefinition[]> {
        return this.getAllNested().pipe(
            map(fieldDefinitions =>
                fieldDefinitions.filter(
                    fieldDefinition => fieldDefinition.DottedKey && dottedKeys.includes(fieldDefinition.DottedKey)
                )
            )
        );
    }

    public override getByDottedKey(dottedKey: string): Observable<BaseFlatFieldDefinition | undefined> {
        return this.getFlatFieldDefinitions().pipe(
            map(fieldDefinitions => fieldDefinitions.find(def => def.DottedKey === dottedKey))
        );
    }

    public override getByProfileIds(
        profileIds: string[],
        dottedKeys: DottedKey[]
    ): Observable<BaseNestedFieldDefinition[]> {
        return this.getAllNested().pipe(
            map(fieldDefinitions => fieldDefinitions.filter(def => dottedKeys.includes(def.DottedKey)))
        );
    }

    getSearchable(): Observable<FieldDefinition[]> {
        return this.getAll().pipe(
            map(fieldDefinitions => fieldDefinitions.filter(fieldDefinition => fieldDefinition.Searchable))
        );
    }

    getFieldDefinitionValues(id: string, params?: SearchParams): Observable<FieldDefinitionValue[]> {
        return this.httpService.get(`${this.ENDPOINT}/${id}/values`, params);
    }

    private enhanceWithParents(fieldDefinitions: FieldDefinition[]): FieldDefinition[] {
        const idRefs: { [key: number]: FieldDefinition } = fieldDefinitions.reduce(
            (hashMap, item) => ({ ...hashMap, [item.FieldDefinitionId]: item }),
            {}
        );

        return fieldDefinitions.map(fieldDefinition => {
            if (fieldDefinition.ParentId) {
                fieldDefinition.Parent = idRefs[fieldDefinition.ParentId];
            }

            return fieldDefinition;
        });
    }

    getFieldDefinition(dottedKey: string): Observable<FieldDefinition | undefined> {
        return this.getAll().pipe(map(defs => defs.filter(def => def.DottedKey === dottedKey)[0]));
    }

    getNestedByDottedKey(dottedKey: string): Observable<BaseNestedFieldDefinition | undefined> {
        return this.getAllNested().pipe(map(defs => defs.filter(def => def.DottedKey === dottedKey)[0]));
    }
}
